"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getFullName, getInitial } from "../../../utils/urlUtils"

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-800">{value || "Not provided"}</span>
  </div>
)

export default function StaffProfile() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}")
      if (u?.role !== "staff") {
        navigate("/dashboard/dashboard", { replace: true })
        return
      }
    } catch {
      navigate("/dashboard/dashboard", { replace: true })
      return
    }
  }, [navigate])

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true)
        const res = await axiosInstance.get(API_ENDPOINTS.usersProfile)
        if (res.data?.status === "success" && res.data?.data?.user) {
          setProfile(res.data.data.user)
        } else {
          setError("Failed to fetch profile data")
        }
      } catch (err) {
        console.error("Staff profile fetch error:", err)
        setError("Failed to fetch profile data. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error && !profile) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-center">
        {error}
      </div>
    )
  }

  if (!profile) return null

  const displayName = profile.name || getFullName(profile) || "Staff"
  const initial = getInitial(profile) || (displayName && displayName.charAt(0)) || "S"

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header Section - same as Teacher MyProfile */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 md:p-6 rounded-t-lg">
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative group">
            {profile.profileImage || profile.profilePicture ? (
              <img
                src={profile.profileImage || profile.profilePicture}
                alt={displayName}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white object-cover"
              />
            ) : (
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white bg-white/20 flex items-center justify-center text-2xl md:text-3xl font-bold text-white">
                {initial}
              </div>
            )}
            <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          </div>
          <div className="text-white">
            <h2 className="text-xl md:text-2xl font-bold">
              {displayName}
              {profile.username && (
                <span className="ml-2 text-lg font-normal text-blue-100">
                  @{profile.username}
                </span>
              )}
            </h2>
            <p className="text-blue-100 mt-1">
              {profile.roleType || "Staff"}
            </p>
          </div>
        </div>
      </div>

      {/* Content Area - same card layout as Teacher */}
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <InfoRow label="Name" value={displayName} />
              <InfoRow label="Username" value={profile.username ? `@${profile.username}` : null} />
              <InfoRow label="Role" value={profile.roleType || "Staff"} />
              <InfoRow label="Staff ID" value={profile.staffId} />
              <InfoRow label="Designation" value={profile.designation} />
              <InfoRow label="Department" value={profile.department} />
              <InfoRow label="Date of Joining" value={profile.dateOfJoining ? new Date(profile.dateOfJoining).toLocaleDateString() : null} />
              <InfoRow label="Marital Status" value={profile.maritalStatus} />
              <InfoRow label="Employment Type" value={profile.teacherType} />
              <InfoRow label="Contract Type" value={profile.contractType} />
              <InfoRow label="Work Shift" value={typeof profile.workShift === "object" && profile.workShift?.shiftName ? profile.workShift.shiftName : (profile.workShift || "—")} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <InfoRow label="Email" value={profile.email} />
              <InfoRow label="Phone Number" value={profile.phoneNumber} />
              <InfoRow label="Street" value={profile.address?.street} />
              <InfoRow label="City" value={profile.address?.city} />
              <InfoRow label="State" value={profile.address?.state} />
              <InfoRow label="Pincode" value={profile.address?.pincode} />
              <InfoRow label="Country" value={profile.address?.country} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Financial Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <InfoRow label="Basic Salary" value={profile.basicSalary ? `₹${profile.basicSalary.toLocaleString()}` : null} />
              <InfoRow label="Allowance" value={profile.allowance ? `₹${profile.allowance.toLocaleString()}` : null} />
              <InfoRow label="PAN Number" value={profile.panNumber} />
              <InfoRow label="EPF Number" value={profile.epf} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Bank Account Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <InfoRow label="Account Holder Name" value={profile.bankAccountDetails?.accountHolderName} />
              <InfoRow label="Account Number" value={profile.bankAccountDetails?.accountNumber} />
              <InfoRow label="Bank Name" value={profile.bankAccountDetails?.bankName} />
              <InfoRow label="IFSC Code" value={profile.bankAccountDetails?.ifsc} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
